/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import type { ReactChild, ReactNode } from 'react';

import { compose } from 'recompose';

import confirmAuthenticated from 'js/lib/auth';
import connectToRouter from 'js/lib/connectToRouter';
import { FormattedMessage } from 'js/lib/coursera.react-intl';
import Retracked from 'js/lib/retracked';
import useRouter from 'js/lib/useRouter';

import { Grid, Link, PageNotification, TabContext, Typography, Typography2, breakpoints } from '@coursera/cds-core';
import type { PageNotificationProps } from '@coursera/cds-core';

import AccomplishmentRecommendations from 'bundles/accomplishments/components/hub/AccomplishmentRecommendations';
import AccountPageHeader from 'bundles/composable-page-header-consumer/header-renderers/AccountPageHeader';
import UniversalPageFeatures from 'bundles/composable-page-header/components/UniversalPageFeatures';
import MyPurchasesTabs from 'bundles/my-purchases-graphql/components/MyPurchasesTabs';
import UpdatePaymentMethodLink from 'bundles/my-purchases-graphql/components/UpdatePaymentMethodLink';
import {
  fetchTabnameByLocation,
  fetchTrackingTabNameByLocation,
} from 'bundles/my-purchases-graphql/components/utils/fetchTabnameByLocation';
import { WalletsProvider, useWalletsContext } from 'bundles/my-purchases-graphql/contexts/WalletsContext';
import { HELP_CENTER_LINK, REFUND_POLICY_LINK } from 'bundles/my-purchases-graphql/lib/constants';
import PageFooter from 'bundles/page-footer/components/PageFooter';
import { TrackedA } from 'bundles/page/components/TrackedLink2';
import SimpleCourseraMetatags from 'bundles/seo/components/SimpleCourseraMetatags';

import _t from 'i18n!nls/my-purchases-graphql';

export type NotificationMetadata = {
  showNotification: boolean;
  notificationProps?: Omit<PageNotificationProps, 'children'>;
  notificationChildren: ReactChild;
};

type NotificationContextType = {
  setNotification: (notification: NotificationMetadata) => void;
};

export const NotificationContext = React.createContext<NotificationContextType>({
  setNotification: () => null,
});

type Props = {
  children: ReactNode;
};

type TrackNameProps = {
  page: string;
};

const MIN_HEIGHT = '140px';

const useStyles = () => {
  return {
    myPurchasesPage: css({
      backgroundColor: 'var(--cds-color-grey-25)',
    }),
    myPurchasesContainer: css({
      margin: '0 auto',
      padding: `var(--cds-spacing-600) var(--cds-spacing-200) 0`,
      maxWidth: '1220px',
    }),
    myPurchasesTabs: css({
      display: 'flex',
      [breakpoints.down('sm')]: {
        display: 'block',
      },
    }),
    paymentMethodLink: css({
      alignSelf: 'flex-end',
      flexShrink: 0,
      borderBottom: '1px solid var(--cds-color-neutral-stroke-primary-weak)',
      paddingBottom: 'var(--cds-spacing-200)',
      [breakpoints.down('sm')]: {
        display: 'flex',
        justifyContent: 'flex-end',
        borderBottom: 0,
        paddingBottom: 0,
        paddingTop: 'var(--cds-spacing-200)',
      },
    }),
    pageNotificationContainer: css({
      position: 'sticky',
      top: 64,
      zIndex: 1000,
      display: 'flex',
      justifyContent: 'center',
    }),
    pageNotification: css({
      maxWidth: 'max-content',
    }),
  };
};

const PaymentMethodLink = () => {
  const styles = useStyles();

  return (
    <div css={styles.paymentMethodLink}>
      <UpdatePaymentMethodLink />
    </div>
  );
};

const MyPurchasesAppContent = ({ children }: Props) => {
  const styles = useStyles();
  const router = useRouter();
  const wallets = useWalletsContext();
  const hasWallet =
    wallets.braintreeWallets.length > 0 || wallets.paypalWallets.length > 0 || wallets.stripeWallets.length > 0;
  const [showNotification, setShowNotification] = useState<NotificationMetadata['showNotification']>(false);
  const [notificationProps, setNotificationProps] = useState<NotificationMetadata['notificationProps']>(undefined);
  const [notificationChildren, setNotificationChildren] = useState<NotificationMetadata['notificationChildren']>('');

  useEffect(() => {
    confirmAuthenticated(router);
  }, [router]);

  const currentTab = useMemo(() => fetchTabnameByLocation(router?.location?.pathname), [router?.location?.pathname]);

  function setNotification(notification: NotificationMetadata) {
    setShowNotification(notification.showNotification);
    setNotificationProps(notification.notificationProps);
    setNotificationChildren(notification.notificationChildren);
  }

  // currentTab must be initialized in order for TabContext to work
  if (!currentTab) return null;

  return (
    <TabContext value={currentTab}>
      <NotificationContext.Provider value={{ setNotification }}>
        <div className="rc-myPurchases" css={styles.myPurchasesPage} style={{ minHeight: MIN_HEIGHT }}>
          <SimpleCourseraMetatags title={_t('My Purchases')} disableCrawlerIndexing />

          <AccountPageHeader />
          <UniversalPageFeatures />

          {showNotification && (
            <div css={styles.pageNotificationContainer}>
              <PageNotification {...notificationProps} css={styles.pageNotification}>
                {notificationChildren}
              </PageNotification>
            </div>
          )}

          <main css={styles.myPurchasesContainer}>
            <Grid container spacing={32}>
              <Grid item xs={12}>
                <Typography component="h1" variant="h1semibold">
                  {_t('Purchases')}
                </Typography>

                <Typography2 component="p" className="helper-links">
                  {_t('Need more help?')}&nbsp;
                  <FormattedMessage
                    message={_t('Check out our {helpCenterLink} and {refundPolicyLink}.')}
                    className="helper-links"
                    helpCenterLink={
                      <Link
                        component={TrackedA}
                        trackingName="my_purchases_help_center_link"
                        href={HELP_CENTER_LINK}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {_t('Learner Help Center')}
                      </Link>
                    }
                    refundPolicyLink={
                      <Link
                        component={TrackedA}
                        trackingName="my_purchases_refund_policies_link"
                        href={REFUND_POLICY_LINK}
                        target="_blank"
                        rel="noopener"
                      >
                        {_t('Terms of Use')}
                      </Link>
                    }
                  />
                </Typography2>
              </Grid>

              <Grid item xs={12}>
                <div css={styles.myPurchasesTabs}>
                  <MyPurchasesTabs currentTab={currentTab}>{hasWallet && <PaymentMethodLink />}</MyPurchasesTabs>
                </div>
              </Grid>

              <Grid item xs={12}>
                <div style={{ minHeight: MIN_HEIGHT }}>{children}</div>
              </Grid>

              <Grid item xs={12}>
                <AccomplishmentRecommendations />
              </Grid>
            </Grid>
          </main>

          <PageFooter />
        </div>
      </NotificationContext.Provider>
    </TabContext>
  );
};

const MyPurchasesApp = (props: Props) => (
  <WalletsProvider>
    <MyPurchasesAppContent {...props} />
  </WalletsProvider>
);

export default compose<Props, {}>(
  connectToRouter((router) => ({
    page: fetchTrackingTabNameByLocation(router?.location?.pathname),
  })),
  Retracked.createTrackedContainer<Props & TrackNameProps>(({ page }) => ({
    namespace: {
      app: 'my_purchases',
      page,
    },
  }))
)(MyPurchasesApp);
